// Day Sheets feature — TypeScript types.
// These mirror the shape returned by the AppSync queries in custom_queries.ts.
// Field names follow the Amplify v1 @model/@connection codegen convention.

import { QuestionnaireType } from 'src/API';

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum DaySheetEyeMode {
  OD_OS = 'OD_OS',
  Single = 'Single',
}

// ─── Model types ──────────────────────────────────────────────────────────────

export type DaySheetPatientRecord = {
  __typename: 'Patient';
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
};

export type DaySheetQuestionnaireRef = {
  __typename: 'QuestionnaireRequest';
  id?: string;
  type?: string | null;
  completedAt?: string | null;
};

export type DaySheetIntakeFormRef = {
  __typename: 'IntakeForm';
  id?: string;
  completedAt?: string | null;
};

// ─── DaySheetPatient ──────────────────────────────────────────────────────────

export type DaySheetPatient = {
  __typename: 'DaySheetPatient';
  id?: string;
  daySheetPatientClinicId?: string;
  daySheetDate?: string;
  patientId?: string;
  // Enriched client-side after create (not returned by deployed backend):
  patient?: DaySheetPatientRecord | null;
  sortOrder?: number | null;
  removedAt?: string | null;
  questionnaireRequest?: DaySheetQuestionnaireRef | null;
  intakeForm?: DaySheetIntakeFormRef | null;
  daySheetPatientTemplateId?: string | null;
  template?: DaySheetTemplate | null;
  daySheetPatientAssessmentId?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type ModelDaySheetPatientConnection = {
  __typename: 'ModelDaySheetPatientConnection';
  items?: Array<DaySheetPatient | null> | null;
  nextToken?: string | null;
};

// ─── DaySheetTemplateSign ─────────────────────────────────────────────────────

export type DaySheetTemplateSign = {
  signId: string;
  label: string;
  eyeMode: DaySheetEyeMode;
  sortOrder: number;
};

// ─── DaySheetTemplate ─────────────────────────────────────────────────────────

export type DaySheetTemplate = {
  __typename: 'DaySheetTemplate';
  id?: string;
  daySheetTemplateClinicSetupId?: string;
  daySheetTemplateClinicId?: string;
  name?: string;
  questionnaires?: string | null;
  assessmentType?: string | null;
  signs?: string | null;
};

export type ModelDaySheetTemplateConnection = {
  __typename: 'ModelDaySheetTemplateConnection';
  items?: Array<DaySheetTemplate | null> | null;
  nextToken?: string | null;
};

// ─── Input types (mutations) ──────────────────────────────────────────────────

export type CreateDaySheetPatientInput = {
  id?: string | null;
  daySheetPatientClinicId: string;
  daySheetDate: string;
  patientId: string;
  daySheetPatientTemplateId: string;
  daySheetPatientAssessmentId?: string | null;
};

export type UpdateDaySheetPatientInput = {
  id: string;
  daySheetPatientAssessmentId?: string | null;
  daySheetPatientTemplateId?: string | null;
};

export type CreateDaySheetTemplateInput = {
  id?: string | null;
  daySheetTemplateClinicSetupId: string;
  daySheetTemplateClinicId: string;
  name: string;
  questionnaires?: string | null;
  assessmentType?: string | null;
  signs?: string | null;
};

export type UpdateDaySheetTemplateInput = {
  id: string;
  name?: string | null;
  questionnaires?: string | null;
  assessmentType?: string | null;
  signs?: string | null;
};

// ─── Query variable / response shapes ────────────────────────────────────────

export type ListDaySheetPatientsByClinicAndDateQueryVariables = {
  daySheetPatientClinicId: string;
  daySheetDate: string;
};

export type ListDaySheetPatientsByClinicAndDateQuery = {
  listDaySheetPatientsByClinicAndDaySheetDate?: ModelDaySheetPatientConnection | null;
};

export type GetDaySheetPatientQueryVariables = {
  id: string;
};

export type GetDaySheetPatientQuery = {
  getDaySheetPatient?: DaySheetPatient | null;
};

export type CreateDaySheetPatientMutationVariables = {
  input: CreateDaySheetPatientInput;
};

export type CreateDaySheetPatientMutation = {
  createDaySheetPatient?: DaySheetPatient | null;
};

export type UpdateDaySheetPatientMutationVariables = {
  input: UpdateDaySheetPatientInput;
};

export type UpdateDaySheetPatientMutation = {
  updateDaySheetPatient?: DaySheetPatient | null;
};

export type ListDaySheetTemplatesByClinicSetupIdQueryVariables = {
  clinicSetupId: string;
};

export type ListDaySheetTemplatesByClinicSetupIdQuery = {
  listDaySheetTemplatesByClinicSetupId?: ModelDaySheetTemplateConnection | null;
};

export type CreateDaySheetTemplateMutationVariables = {
  input: CreateDaySheetTemplateInput;
};

export type CreateDaySheetTemplateMutation = {
  createDaySheetTemplate?: DaySheetTemplate | null;
};

export type UpdateDaySheetTemplateMutationVariables = {
  input: UpdateDaySheetTemplateInput;
};

export type UpdateDaySheetTemplateMutation = {
  updateDaySheetTemplate?: DaySheetTemplate | null;
};

// ─── Derived / display-only types ─────────────────────────────────────────────

// 'not_required' means no questionnaire was found for this patient.
export type DaySheetQuestionnaireStatus = 'completed' | 'pending' | 'not_required';

export type DaySheetIntakeStatus = 'completed' | 'pending' | 'not_sent';

